import type { Game } from '@fantaz/domain/games';
import type { Metadata } from '@fantaz/shared/data-access/models';

type ImageSrcSet = {
  image: string;
  sm?: string;
  md?: string;
  lg?: string;
};

const getDefaultImage = (
  game: Game,
  type: 'avatar' | 'background' = 'background',
): string => {
  const result = game?.key?.split('.');
  const [gameName, version] = result || ['default', 'v1'];

  if (type === 'background') {
    return `/shared/img/games/${gameName}/${version}/${gameName}-background.webp`;
  }

  return `/shared/img/games/${gameName}/${version}/${gameName}-${type}.webp`;
};

export const getImagePublicId = (url: string): string => {
  const result = url
    .replace('com-', '')
    .replace('-assets.s3.us-east-1.amazonaws', '');

  // add www if not present, use regex to match with https://www.
  if (!result.match(/^https?:\/\/www\./)) {
    return result.replace(/^https?:\/\//, 'https://www.');
  }

  return result;
};

/**
 * Build image src set for banner
 */
export const buildImageSrcSet = (
  metadata: Metadata,
  game: Game,
): ImageSrcSet => {
  // if use metadata do not have a new banner images format then use old format
  if (!metadata || !metadata.bannerImages) {
    // in case of old tournaments use the game banner
    if (!metadata.fullSizeBannerImage) {
      const defaultImage = getDefaultImage(game);
      return {
        image: defaultImage,
        sm: defaultImage,
        md: defaultImage,
        lg: defaultImage,
      };
    }

    return {
      image: metadata?.fullSizeBannerImage || '',
      sm:
        metadata?.mobileSizeBannerImage || metadata?.fullSizeBannerImage || '',
      md:
        metadata?.mediumSizeBannerImage || metadata?.fullSizeBannerImage || '',
      lg: metadata?.fullSizeBannerImage || metadata?.fullSizeBannerImage || '',
    };
  }

  // get small
  let lg = '';
  let md = '';
  let sm = '';

  if (metadata.bannerImages.desktop) {
    lg = metadata.bannerImages.desktop.url;
    md = metadata.bannerImages.desktop?.formats?.md?.url || lg;
    sm = metadata.bannerImages.desktop?.formats?.sm?.url || lg;
  }

  if (metadata.bannerImages.tablet) {
    md = metadata.bannerImages.tablet.url;
    sm = metadata.bannerImages.tablet?.formats?.sm?.url || md;
  }

  if (metadata.bannerImages.mobile) {
    sm = metadata.bannerImages.mobile.url;
  }

  return {
    image: getImagePublicId(lg),
    sm: getImagePublicId(sm),
    md: getImagePublicId(md),
    lg: getImagePublicId(lg),
  };
};
